import React from "react";
import { Colors, FontSize } from "../../theme/Theme";
import { RBPayrollVerifiedBadge } from "../../pages/searchShift/RBPayrollVerifiedBadge";
import { LocationManagedBadge } from "./LocationManagedBadge";
import {
  resolveShiftPaymentIndicator,
  ShiftPaymentIndicator,
} from "../../util/rbPay";

interface ShiftPaymentFieldProps {
  shift?: any;
  /** Pre-resolved indicator, when the caller already needed it (e.g. for the note). */
  indicator?: ShiftPaymentIndicator;
  /** One-row "Shift Payment:  [badge]" layout for cards; otherwise title above value. */
  inline?: boolean;
  style?: React.CSSProperties;
}

/**
 * US6559 Phase 2 — the "Shift Payment" field.
 *
 * Tells a worker, before they apply, how they will be paid for this shift:
 *   RB Pay Verified (green) -> the shift follows the RB Pay workflow
 *   Location Managed (grey) -> the location pays them directly
 *
 * The component owns the decision: pass it the shift and it renders nothing when the field
 * does not apply (network-paid shifts, or a shift with no pharmacy). Pages must NOT
 * re-derive the rule — resolveShiftPaymentIndicator in util/rbPay.ts is the one place it
 * lives.
 *
 * `inline` picks the layout:
 *   true  -> "Shift Payment:  [badge]" on one row (search / schedule cards, where vertical
 *            space is scarce and the row sits beside the network icon)
 *   false -> title above, badge below (detail pages, matching the sibling Comment /
 *            Duration / Distance fields)
 */
export const ShiftPaymentField: React.FC<ShiftPaymentFieldProps> = ({
  shift,
  indicator,
  inline = false,
  style,
}) => {
  const resolved = indicator || resolveShiftPaymentIndicator(shift);

  if (!resolved.showField) {
    return null;
  }

  const badge = resolved.isRBPay ? (
    <RBPayrollVerifiedBadge />
  ) : (
    <LocationManagedBadge />
  );

  if (inline) {
    // Shrink rules matter here: this row competes for width with the network icon and the
    // "View Multiple Shifts" button. Both the label and the badge are nowrap, so a flex box
    // that is merely allowed to shrink would overflow its bounds rather than reflow. The
    // label is therefore given minWidth: 0 + ellipsis so it degrades gracefully, while the
    // badge is pinned at its natural width — truncating "Shift Payment:" is recoverable,
    // truncating the value it labels is not.
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          minWidth: 0,
          ...style,
        }}
      >
        {/*
          Card variant deliberately does NOT use the grey field-label colour. Cards have no
          grey labels — every other line on them (date, time, hours, distance) is default
          body text — so a grey label reads as washed-out caption text beside them. The
          stacked variant below keeps the grey, because there it sits next to
          Comment/Duration and should match those.
        */}
        <span
          style={{
            fontSize: FontSize.default,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          Shift Payment:
        </span>
        <div style={{ flexShrink: 0 }}>{badge}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column", ...style }}>
      <div style={{ color: Colors.darkGrey, fontSize: FontSize.default }}>
        Shift Payment
      </div>
      <div style={{ marginTop: 2 }}>{badge}</div>
    </div>
  );
};
